import { i18n } from './config';
import { readStoredLanguage, type AppLanguage } from './locale';

let runtimeLanguage: AppLanguage = readStoredLanguage();

const MONTHS: Record<string, string> = {
  января: 'January',
  февраля: 'February',
  марта: 'March',
  апреля: 'April',
  мая: 'May',
  июня: 'June',
  июля: 'July',
  августа: 'August',
  сентября: 'September',
  октября: 'October',
  ноября: 'November',
  декабря: 'December',
};

export function setRuntimeLanguage(language: AppLanguage) {
  runtimeLanguage = language;
}

function preserveOuterWhitespace(source: string, translated: string) {
  const leading = source.match(/^\s*/)?.[0] ?? '';
  const trailing = source.match(/\s*$/)?.[0] ?? '';
  return `${leading}${translated}${trailing}`;
}

function translateKnownText(value: string) {
  return i18n.getFixedT('en')(value, { defaultValue: value });
}

function englishCount(value: string, singular: string, plural = `${singular}s`) {
  const numeric = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(numeric) && numeric === 1 ? singular : plural;
}

function replaceMonth(value: string) {
  return value.replace(
    new RegExp(`(${Object.keys(MONTHS).join('|')})`, 'giu'),
    (month) => MONTHS[month.toLocaleLowerCase('ru-RU')] ?? month,
  );
}

function translateDynamicUiText(value: string): string | null {
  let match: RegExpMatchArray | null;

  match = value.match(/^Регистрация (заказчика|перевозчика) \((\d+)\/(\d+)\)$/u);
  if (match) return `${match[1] === 'заказчика' ? 'Customer' : 'Carrier'} registration (${match[2]}/${match[3]})`;
  match = value.match(/^Регистрация \((\d+)\/(\d+)\)$/u);
  if (match) return `Registration (${match[1]}/${match[2]})`;
  match = value.match(/^Шаг (\d+)$/u);
  if (match) return `Step ${match[1]}`;

  match = value.match(/^Цифра (\d+)$/u);
  if (match) return `Digit ${match[1]}`;

  match = value.match(/^Не пришёл код\? Отправить снова(?: через (.+))?$/u);
  if (match) return `No code? Send again${match[1] ? ` in ${match[1]}` : ''}`;

  match = value.match(/^Укажите корректный год для даты (забора|доставки) \((\d+)–(\d+)\)\.$/u);
  if (match) return `Enter a valid year for the ${match[1] === 'забора' ? 'pickup' : 'delivery'} date (${match[2]}–${match[3]}).`;

  match = value.match(/^Укажите корректный год выпуска авто \((\d+)–(\d+)\)\.$/u);
  if (match) return `Enter a valid vehicle year (${match[1]}–${match[2]}).`;

  match = value.match(/^Где забрать · город (.+)$/u);
  if (match) return `Pickup · city ${translateKnownText(match[1])}`;
  match = value.match(/^Куда доставить · город (.+)$/u);
  if (match) return `Delivery · city ${translateKnownText(match[1])}`;
  match = value.match(/^(.+) \(адрес — в чате\)$/u);
  if (match) return `${translateKnownText(match[1])} (address shared in chat)`;

  match = value.match(/^Фото (питомца|авто|из портфолио) (\d+)$/u);
  if (match) {
    const subject = match[1] === 'питомца' ? 'Pet photo' : match[1] === 'авто' ? 'Vehicle photo' : 'Portfolio photo';
    return `${subject} ${match[2]}`;
  }
  match = value.match(/^Удалить фото(?: авто)? (\d+)$/u);
  if (match) return `Delete photo ${match[1]}`;
  match = value.match(/^Просмотреть документ: (.+)$/u);
  if (match) return `View document: ${translateKnownText(match[1])}`;
  match = value.match(/^Название лицензии (\d+)$/u);
  if (match) return `Licence name ${match[1]}`;
  match = value.match(/^Удалить (.+)$/u);
  if (match) return `Delete ${match[1]}`;

  match = value.match(/^от ([\d\s.,]+) ₽$/u);
  if (match) return `from ₽${match[1].trim()}`;
  match = value.match(/^ОТ ([\d\s.,]+) ₽$/u);
  if (match) return `FROM ₽${match[1].trim()}`;
  match = value.match(/^до ([\d\s.,]+) ₽$/u);
  if (match) return `up to ₽${match[1].trim()}`;

  match = value.match(/^([\d.,]+)\s*кг$/u);
  if (match) {
    const num = Number.parseFloat(match[1].replace(',', '.'));
    return `${Number.isFinite(num) ? num : match[1]} kg`;
  }
  match = value.match(/^([\d.,]+)\s*kg$/iu);
  if (match) {
    const num = Number.parseFloat(match[1].replace(',', '.'));
    return `${Number.isFinite(num) ? num : match[1]} kg`;
  }
  match = value.match(/^([\d.,]+) шт\.$/u);
  if (match) return `${match[1]} ${englishCount(match[1], 'pet')}`;
  match = value.match(/^(\d+) питомца$/u);
  if (match) return `${match[1]} pets`;
  match = value.match(/^(\d+) (?:год|года|лет)$/u);
  if (match) return `${match[1]} ${englishCount(match[1], 'year')}`;
  match = value.match(/^(\d+) (?:фото|фотографии)$/u);
  if (match) return `${match[1]} photos`;
  match = value.match(/^На основе (\d+) (?:отзыва|отзывов)$/u);
  if (match) return `Based on ${match[1]} ${englishCount(match[1], 'review')}`;
  match = value.match(/^(\d+) поездок$/u);
  if (match) return `${match[1]} trips`;
  match = value.match(/^(\d+) перевозчиков$/u);
  if (match) return `${match[1]} carriers`;
  match = value.match(/^(\d+) (?:отклик|отклика|откликов)$/u);
  if (match) return `${match[1]} ${englishCount(match[1], 'offer')}`;
  match = value.match(/^(\d+) файлов$/u);
  if (match) return `${match[1]} files`;
  match = value.match(/^Загружено файлов: (\d+)$/u);
  if (match) return `Files uploaded: ${match[1]}`;

  match = value.match(/^(\d+) из 5$/u);
  if (match) return `${match[1]} out of 5`;
  match = value.match(/^(.+): (\d+) из 5$/u);
  if (match) return `${translateKnownText(match[1])}: ${match[2]} out of 5`;
  match = value.match(/^Загрузка (\d+)%$/u);
  if (match) return `Uploading ${match[1]}%`;

  match = value.match(/^(\d+) мин\.? назад$/u);
  if (match) return `${match[1]} min ago`;
  if (value === 'секунду назад') return 'a second ago';
  match = value.match(/^(\d+) (?:секунду|секунды|секунд) назад$/u);
  if (match) return `${match[1]} sec ago`;
  match = value.match(/^(\d+) ч\.? назад$/u);
  if (match) return `${match[1]} hr ago`;
  match = value.match(/^(\d+) дн\.? назад$/u);
  if (match) return `${match[1]} days ago`;
  match = value.match(/^(\d+) (месяц|месяца|месяцев) назад$/u);
  if (match) return `${match[1]} ${englishCount(match[1], 'month')} ago`;
  if (value === 'только что') return 'just now';

  match = value.match(/^выезд (.+)$/u);
  if (match) return `departure ${replaceMonth(match[1])}`;
  match = value.match(/^последняя (.+)$/u);
  if (match) return `latest ${translateDynamicUiText(match[1]) ?? translateKnownText(match[1])}`;
  match = value.match(/^до (\d{1,2}(?:–\d{1,2})? [а-яё]+(?: \d{4})?)$/u);
  if (match) return `by ${replaceMonth(match[1])}`;
  match = value.match(/^(\d{1,2})(\s*–\s*\d{1,2})? ([а-яё]+)(, срочно)?$/u);
  if (match && MONTHS[match[3]]) return `${replaceMonth(`${match[1]}${match[2] ?? ''} ${match[3]}`)}${match[4] ? ', urgent' : ''}`;

  match = value.match(/^Вход через (.+) требует подключения внешнего сервиса\.$/u);
  if (match) return `${match[1]} login requires an external service connection.`;
  match = value.match(/^Email (.+) подтвердите потом в личном кабинете$/u);
  if (match) return `Confirm ${match[1]} later in your account`;
  match = value.match(/^После создания аккаунта отправим подтверждение на(.*)$/u);
  if (match) return `We will send a confirmation${match[1].trim() ? ` to ${match[1].trim()}` : ''} after account creation`;
  match = value.match(/^✓ Телефон подтверждён:(.*)$/u);
  if (match) return `✓ Phone confirmed:${match[1]}`;
  match = value.match(/^Отправили на (.*)\. Подтверждаем телефон(?: сейчас)? — email подтвердите потом в профиле\.$/u);
  if (match) return `Sent to ${match[1]}. We are confirming your phone now; confirm your email later in your profile.`;
  match = value.match(/^📧 Аккаунта с email (.*) ещё нет — создадим новый$/u);
  if (match) return `📧 No account exists for ${match[1]}; we will create one.`;
  match = value.match(/^Аккаунта с email (.*) ещё нет — создадим новый$/u);
  if (match) return `No account exists for ${match[1]}; we will create one.`;
  match = value.match(/^Сейчас в портфолио: (\d+) фото · ориентир для топ-перевозчика: (\d+)\+ фото$/u);
  if (match) return `Portfolio now: ${match[1]} photos · top carriers usually have ${match[2]}+ photos`;

  match = value.match(/^кличка: (.+)$/u);
  if (match) return `pet name: ${match[1]}`;
  match = value.match(/^Перевозчик\s+([A-ZА-ЯЁ][a-zа-яё]+(?:\s+[A-ZА-ЯЁ]\.?)?|#\w+)$/u);
  if (match) return `Carrier ${match[1]}`;
  match = value.match(/^Заказчик: (.+)$/u);
  if (match) return `Customer: ${match[1]}`;
  match = value.match(/^Заявка #(.*)$/u);
  if (match) return `Request #${match[1]}`;
  match = value.match(/^забор с (.+) · доставка до (.+)$/u);
  if (match) return `pickup from ${replaceMonth(match[1])} · delivery by ${replaceMonth(match[2])}`;
  match = value.match(/^Публичные вопросы · (\d+)$/u);
  if (match) return `Public questions · ${match[1]}`;
  match = value.match(/^Перевозчики спрашивают · (\d+) (?:вопрос|вопроса|вопросов)$/u);
  if (match) return `Questions from carriers · ${match[1]} ${englishCount(match[1], 'question')}`;
  match = value.match(/^Отклики перевозчиков · (\d+)$/u);
  if (match) return `Carrier offers · ${match[1]}`;
  match = value.match(/^Заказчик · на LOVO с (.+)$/u);
  if (match) return `Customer · on LOVO since ${replaceMonth(match[1])}`;
  match = value.match(/^Перевозчик с (.+)$/u);
  if (match) return `Carrier since ${replaceMonth(match[1])}`;

  match = value.match(/^День (\d+)\/(\d+) · в пути$/u);
  if (match) return `Day ${match[1]}/${match[2]} · in transit`;
  match = value.match(/^день (\d+)\/(\d+)$/u);
  if (match) return `day ${match[1]}/${match[2]}`;
  match = value.match(/^Открыть перевозку (.+)$/u);
  if (match) return `Open transport ${match[1]}`;
  match = value.match(/^Открыть отклик (.+)$/u);
  if (match) return `Open offer ${match[1]}`;
  match = value.match(/^(Выставлен|Оплачен|Списание|Возврат по) сч[её]т(?: комиссии)?(.*)$/u);
  if (match) {
    const operation: Record<string, string> = { Выставлен: 'Fee invoice issued', Оплачен: 'Fee invoice paid', Списание: 'Fee invoice debit', 'Возврат по': 'Invoice refund' };
    return `${operation[match[1]]}${match[2]}`;
  }
  match = value.match(/^Оплатить (.+) картой$/u);
  if (match) return `Pay ${match[1]} by card`;
  match = value.match(/^Не оплачен сч[её]т #(.+)$/u);
  if (match) return `Unpaid invoice #${match[1]}`;
  match = value.match(/^Сегодня на ваших маршрутах — (\d+) (.+)\.(.*)$/u);
  if (match) return `Today on your routes: ${match[1]} new ${englishCount(match[1], 'request')}.${match[3] ? ' One transport is already in progress.' : ''}`;
  match = value.match(/^На ваших маршрутах — (\d+) (.+)\.(.*)$/u);
  if (match) return `On your routes: ${match[1]} open ${englishCount(match[1], 'request')}.${match[3] ? ' One transport is already in progress.' : ''}`;
  match = value.match(/^Здравствуйте, (.+)!$/u);
  if (match) return `Hello, ${match[1]}!`;
  match = value.match(/^сейчас(?: в)?: (.+)$/u);
  if (match) return `now: ${match[1]}`;
  match = value.match(/^сейчас в (.+)$/u);
  if (match) return `now at ${match[1]}`;
  match = value.match(/^Открытый счёт #(\S+) на (.+) — не оплачен(, отклики заблокированы)?\.$/u);
  if (match) return `Open invoice #${match[1]} for ${match[2]} is unpaid${match[3] ? '; offers are blocked' : ''}.`;

  return null;
}

const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  А: 'A', Б: 'B', В: 'V', Г: 'G', Д: 'D', Е: 'E', Ё: 'Yo', Ж: 'Zh',
  З: 'Z', И: 'I', Й: 'Y', К: 'K', Л: 'L', М: 'M', Н: 'N', О: 'O',
  П: 'P', Р: 'R', С: 'S', Т: 'T', У: 'U', Ф: 'F', Х: 'Kh', Ц: 'Ts',
  Ч: 'Ch', Ш: 'Sh', Щ: 'Shch', Ъ: '', Ы: 'Y', Ь: '', Э: 'E', Ю: 'Yu', Я: 'Ya',
};

export function transliterateCyrillic(value: string): string {
  return value.replace(/[а-яёА-ЯЁ]/gu, (ch) => CYRILLIC_TO_LATIN[ch] ?? ch);
}

export function translateUiText(value: string): string {
  if (runtimeLanguage !== 'en' || !/[\u0400-\u04ff]/u.test(value)) return value;

  const normalized = value.replace(/\s+/gu, ' ').trim();
  if (!normalized) return value;

  const known = translateKnownText(normalized);
  if (known !== normalized) return preserveOuterWhitespace(value, known);

  const dynamic = translateDynamicUiText(normalized);
  if (dynamic) return preserveOuterWhitespace(value, dynamic);

  if (normalized.length <= 40 && !/[.!?]/u.test(normalized)) {
    const transliterated = transliterateCyrillic(normalized);
    return preserveOuterWhitespace(value, transliterated);
  }

  return value;
}

function localizeChild(value: unknown): unknown {
  if (typeof value === 'string') return translateUiText(value);
  if (Array.isArray(value)) return value.map(localizeChild);
  return value;
}

export function localizeProps<T extends Record<string, unknown> | null>(props: T): T {
  if (runtimeLanguage !== 'en' || !props) return props;

  const localizedStringProps = new Set(['aria-label', 'aria-description', 'placeholder', 'title', 'alt']);
  let localized: Record<string, unknown> | undefined;
  for (const [key, value] of Object.entries(props)) {
    const nextValue = key === 'children'
      ? localizeChild(value)
      : localizedStringProps.has(key) && typeof value === 'string'
        ? translateUiText(value)
        : value;
    if (nextValue !== value) {
      localized ??= { ...props };
      localized[key] = nextValue;
    }
  }
  return (localized ?? props) as T;
}
